/**
 * Admin Notification Center — settings, events, recipients, delivery log.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import SectionTabs, { SectionTab } from '../components/SectionTabs';
import { Alert, PageHeader, Toggle } from '../components/ui';
import api from '../services/api';

const TABS: SectionTab[] = [
  { id: 'settings', label: 'Settings' },
  { id: 'events', label: 'Events' },
  { id: 'recipients', label: 'Recipients' },
  { id: 'logs', label: 'Delivery log' },
];

type RecipientMode = 'all_users' | 'admins_only' | 'custom';

interface SettingsForm {
  notificationsEnabled: boolean;
  recipientMode: RecipientMode;
  notifyOnCreate: boolean;
  notifyOnUpdate: boolean;
  notifyOnDelete: boolean;
  updateThrottleMinutes: number;
  includeChangeDetails: boolean;
}

interface CatalogEvent {
  key: string;
  model: string;
  trigger: string;
  entityLabel: string;
  enabled: boolean;
}

interface RecipientRow {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  roleName: string;
  adminEnabled: boolean;
  selfOptOut: boolean;
}

interface EmailLogRow {
  id: number;
  event_key?: string | null;
  eventKey?: string | null;
  template_key?: string | null;
  templateKey?: string | null;
  recipient_email?: string;
  recipientEmail?: string;
  status: string;
  created_at?: string;
  createdAt?: string;
  error_message?: string | null;
  errorMessage?: string | null;
}

const defaultSettings: SettingsForm = {
  notificationsEnabled: true,
  recipientMode: 'all_users',
  notifyOnCreate: true,
  notifyOnUpdate: true,
  notifyOnDelete: true,
  updateThrottleMinutes: 15,
  includeChangeDetails: true,
};

const NotificationCenter: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = TABS.some((t) => t.id === tabParam) ? (tabParam as string) : 'settings';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<any>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [provider, setProvider] = useState('console');
  const [settings, setSettings] = useState<SettingsForm>(defaultSettings);
  const [events, setEvents] = useState<CatalogEvent[]>([]);
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [logs, setLogs] = useState<EmailLogRow[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const handleTabChange = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get('/api/notifications/settings');
      setGlobalEnabled(Boolean(data.globalEnabled));
      setProvider(data.provider || 'console');
      setSettings({
        notificationsEnabled: Boolean(data.settings?.notificationsEnabled),
        recipientMode: data.settings?.recipientMode || 'all_users',
        notifyOnCreate: Boolean(data.settings?.notifyOnCreate),
        notifyOnUpdate: Boolean(data.settings?.notifyOnUpdate),
        notifyOnDelete: Boolean(data.settings?.notifyOnDelete),
        updateThrottleMinutes: Number(data.settings?.updateThrottleMinutes) || 15,
        includeChangeDetails: Boolean(data.settings?.includeChangeDetails),
      });
      setEvents(Array.isArray(data.events) ? data.events : []);
      setRecipients(Array.isArray(data.recipients) ? data.recipients : []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const { data } = await api.get('/api/notifications/logs', { params: { limit: 50 } });
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (activeTab === 'logs') {
      void loadLogs();
    }
  }, [activeTab, loadLogs]);

  const eventsByModel = useMemo(() => {
    const map = new Map<string, CatalogEvent[]>();
    for (const e of events) {
      const label = e.entityLabel || e.model;
      const list = map.get(label) || [];
      list.push(e);
      map.set(label, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [events]);

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      await api.put('/api/notifications/settings', settings);
      setSuccessMessage('Notification settings saved.');
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const toggleEvent = async (eventKey: string, enabled: boolean) => {
    try {
      setError(null);
      await api.put(`/api/notifications/events/${encodeURIComponent(eventKey)}`, { enabled });
      setEvents((prev) =>
        prev.map((e) => (e.key === eventKey ? { ...e, enabled } : e))
      );
    } catch (err) {
      setError(err);
    }
  };

  const toggleRecipient = async (userId: number, adminEnabled: boolean) => {
    try {
      setError(null);
      await api.put(`/api/notifications/recipients/${userId}`, { adminEnabled });
      setRecipients((prev) =>
        prev.map((r) => (r.userId === userId ? { ...r, adminEnabled } : r))
      );
    } catch (err) {
      setError(err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-7">
      <PageHeader
        title="Notification Center"
        subtitle="Control who receives email alerts and which events are sent for this winery."
      />

      {!globalEnabled && (
        <Alert
          type="warning"
          message="Server-wide notifications are disabled (NOTIFICATIONS_ENABLED). Winery settings below will not send mail until the global flag is turned on."
        />
      )}

      {successMessage && (
        <Alert
          type="success"
          message={successMessage}
          autoHide
          onClose={() => setSuccessMessage(null)}
        />
      )}
      <ErrorMessageComponent error={error} />

      <SectionTabs tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Settings */}
      <div hidden={activeTab !== 'settings'}>
        <div className="bg-white rounded-2xl border border-monday-border shadow-xs p-5 sm:p-6 space-y-6 max-w-2xl">
          <p className="text-xs text-monday-text-muted">
            Email provider: <span className="font-medium text-monday-text">{provider}</span>
          </p>

          <Toggle
            label="Enable notifications for this winery"
            description="Master switch. When off, no winery emails are sent (global kill switch still applies)."
            checked={settings.notificationsEnabled}
            onChange={(e) =>
              setSettings((s) => ({ ...s, notificationsEnabled: e.target.checked }))
            }
          />

          <div>
            <label className="block text-sm font-medium text-monday-text mb-1.5">
              Who receives emails
            </label>
            <select
              className="w-full rounded-xl border border-monday-border bg-white px-3 py-2 text-sm"
              value={settings.recipientMode}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  recipientMode: e.target.value as RecipientMode,
                }))
              }
            >
              <option value="all_users">All active winery users</option>
              <option value="admins_only">Admins only</option>
              <option value="custom">Custom list (Recipients tab)</option>
            </select>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-monday-text">Notify on</p>
            <Toggle
              label="Create"
              checked={settings.notifyOnCreate}
              onChange={(e) =>
                setSettings((s) => ({ ...s, notifyOnCreate: e.target.checked }))
              }
            />
            <Toggle
              label="Update"
              checked={settings.notifyOnUpdate}
              onChange={(e) =>
                setSettings((s) => ({ ...s, notifyOnUpdate: e.target.checked }))
              }
            />
            <Toggle
              label="Delete"
              checked={settings.notifyOnDelete}
              onChange={(e) =>
                setSettings((s) => ({ ...s, notifyOnDelete: e.target.checked }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-monday-text mb-1.5">
              Update throttle (minutes)
            </label>
            <input
              type="number"
              min={0}
              max={1440}
              className="w-32 rounded-xl border border-monday-border bg-white px-3 py-2 text-sm"
              value={settings.updateThrottleMinutes}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  updateThrottleMinutes: Number(e.target.value) || 0,
                }))
              }
            />
            <p className="text-xs text-monday-text-muted mt-1">
              Minimum time between update emails for the same record. 0 = no throttle.
            </p>
          </div>

          <Toggle
            label="Include field details in emails"
            description="Show create/delete snapshots and update field diffs in the message body."
            checked={settings.includeChangeDetails}
            onChange={(e) =>
              setSettings((s) => ({ ...s, includeChangeDetails: e.target.checked }))
            }
          />

          <div className="pt-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => void saveSettings()}
              className="mobile-btn mobile-btn-primary"
            >
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </div>
      </div>

      {/* Events */}
      <div hidden={activeTab !== 'events'}>
        <div className="space-y-6">
          {eventsByModel.map(([label, list]) => (
            <div
              key={label}
              className="bg-white rounded-2xl border border-monday-border shadow-xs overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-monday-border bg-monday-header/40">
                <h3 className="text-sm font-semibold text-monday-text">{label}</h3>
              </div>
              <ul className="divide-y divide-monday-border">
                {list.map((ev) => (
                  <li
                    key={ev.key}
                    className="flex items-center justify-between gap-4 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-monday-text truncate">
                        {ev.key}
                      </p>
                      <p className="text-xs text-monday-text-muted capitalize">
                        {ev.trigger}
                      </p>
                    </div>
                    <Toggle
                      checked={ev.enabled}
                      onChange={(e) => void toggleEvent(ev.key, e.target.checked)}
                      aria-label={`Toggle ${ev.key}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {events.length === 0 && (
            <p className="text-sm text-monday-text-muted">No catalog events found.</p>
          )}
        </div>
      </div>

      {/* Recipients */}
      <div hidden={activeTab !== 'recipients'}>
        {settings.recipientMode !== 'custom' && (
          <Alert
            type="info"
            message={`Recipient mode is "${settings.recipientMode.replace('_', ' ')}". Switch to "Custom list" in Settings for per-user admin toggles to take effect. Self opt-outs always apply.`}
          />
        )}
        <div className="mt-4 bg-white rounded-2xl border border-monday-border shadow-xs overflow-hidden">
          <table className="min-w-full divide-y divide-monday-border">
            <thead className="bg-monday-header/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wide">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wide">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wide">
                  Receive mail
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-monday-text-muted uppercase tracking-wide">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-monday-border">
              {recipients.map((r) => (
                <tr key={r.userId}>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-monday-text">
                      {r.firstName} {r.lastName}
                    </div>
                    <div className="text-xs text-monday-text-muted">{r.email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm capitalize text-monday-text-muted">
                    {r.roleName || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Toggle
                      checked={r.adminEnabled}
                      disabled={r.selfOptOut}
                      onChange={(e) => void toggleRecipient(r.userId, e.target.checked)}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {r.selfOptOut ? (
                      <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-800 px-2.5 py-0.5 text-xs font-medium">
                        Opted out
                      </span>
                    ) : (
                      <span className="text-xs text-monday-text-muted">Active</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {recipients.length === 0 && (
            <p className="p-4 text-sm text-monday-text-muted">No active users in this winery.</p>
          )}
        </div>
      </div>

      {/* Delivery log */}
      <div hidden={activeTab !== 'logs'}>
        {logsLoading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-monday-border shadow-xs overflow-x-auto">
            <table className="min-w-full divide-y divide-monday-border">
              <thead className="bg-monday-header/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-monday-text-muted uppercase">
                    When
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-monday-text-muted uppercase">
                    Event
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-monday-text-muted uppercase">
                    To
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-monday-text-muted uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-monday-border">
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-4 py-3 text-xs text-monday-text-muted whitespace-nowrap">
                      {log.createdAt || log.created_at
                        ? new Date(
                            (log.createdAt || log.created_at) as string
                          ).toLocaleString()
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-monday-text">
                      {log.eventKey || log.event_key || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-monday-text">
                      {log.recipientEmail || log.recipient_email || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm capitalize">{log.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {logs.length === 0 && (
              <p className="p-4 text-sm text-monday-text-muted">No delivery logs yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;
